import styled from 'styled-components';
import { useComputeGeoDeviationsMutation, useGetGeoTolerancesQuery } from '@shared/api';
import {
  CONTOUR_MODES,
  deviationItemLabel,
  fmtDateTime,
  PILE_LAYOUTS,
  WORK_TYPES,
  type GeoSettings,
  type GeoToleranceOverrides,
  type GeoWorkType,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import {
  AppliedTolerance,
  Check,
  ChoiceCard,
  ChoiceGrid,
  DataTable,
  DeviationTable,
  DeviationTotals,
  DeviationWarnings,
  ErrorText,
  FieldGrid,
  FieldLabel,
  FieldNote,
  Fieldset,
  Hint,
  Muted,
  Notice,
  NumInput,
  Panel,
  PanelHead,
  PanelTitle,
  Row,
  Stack,
  TableWrap,
  useAction,
  useDraft,
} from '@widgets/GeoSchemes';
import { StepShell, useSettingsSaver, useStepDirty, useWizard } from './wizard';

type Agreement = NonNullable<GeoSettings['agreement']>;

const OVERRIDE_FIELDS: ReadonlyArray<{ key: keyof GeoToleranceOverrides; label: string }> = [
  { key: 'plan_mm', label: 'План, ±мм (общий)' },
  { key: 'plan_along_mm', label: 'План вдоль оси, ±мм' },
  { key: 'plan_across_mm', label: 'План поперёк оси, ±мм' },
  { key: 'height_plus_mm', label: 'Высота «+», мм' },
  { key: 'height_minus_mm', label: 'Высота «−», мм' },
  { key: 'vertical_mm', label: 'Вертикальность, мм' },
];

const NormRef = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ToleranceValue = styled.b`
  font-weight: 600;
  white-space: nowrap;
`;

const Names = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12.5px;
  line-height: 1.6;
  overflow-wrap: anywhere;
`;

const EMPTY_AGREEMENT: Agreement = { organization: '', document: '', date: '' };

/** Шаг 7. Тип работ, какие отклонения ставить, допуски и расчёт сличения. */
export function StepDeviations() {
  const { scheme, readOnly } = useWizard();
  const { data: tolData, isError: tolError } = useGetGeoTolerancesQuery();
  const workType = useDraft<GeoWorkType>(scheme.work_type);
  const work = useDraft(scheme.settings.work);
  const agreement = useDraft<Agreement>(scheme.settings.agreement ?? EMPTY_AGREEMENT);
  const dirty = workType.dirty || work.dirty || agreement.dirty;
  useStepDirty('work', dirty && !readOnly);
  const saver = useSettingsSaver();
  const [compute] = useComputeGeoDeviationsMutation();
  const computing = useAction();

  const types = tolData?.work_types ?? [];
  const info = types.find((t) => t.code === workType.draft);
  const w = work.draft;
  const report = scheme.deviations;
  const a = agreement.draft;
  const agreementFilled =
    a.organization.trim() !== '' || a.document.trim() !== '' || a.date.trim() !== '';
  const showAgreement = (report?.totals.exceeded ?? 0) > 0 || agreementFilled;

  function pickType(code: GeoWorkType) {
    workType.set(code);
    const t = types.find((x) => x.code === code);
    if (t) {
      work.set({
        ...work.draft,
        measure_plan: t.measures.plan,
        measure_height: t.measures.height,
        measure_verticality: t.measures.verticality,
      });
    }
  }

  function setOverride(key: keyof GeoToleranceOverrides, v: number | null) {
    work.set({
      ...w,
      tolerance_overrides: { ...w.tolerance_overrides, [key]: v == null ? null : Math.abs(v) },
    });
  }

  async function save(): Promise<boolean> {
    const ok = await saver.save(
      (cur) => ({
        ...cur,
        work: w,
        agreement: agreementFilled
          ? {
              organization: a.organization.trim(),
              document: a.document.trim(),
              date: a.date.trim(),
            }
          : null,
      }),
      workType.draft !== scheme.work_type ? { work_type: workType.draft } : {},
    );
    if (ok) {
      workType.reset();
      work.reset();
      agreement.reset();
    }
    return ok;
  }

  async function runCompute() {
    if (dirty && !readOnly) {
      const ok = await save();
      if (!ok) return;
    }
    await computing.run(() => compute(scheme.id).unwrap(), 'Расчёт отклонений не выполнен.');
  }

  const blockedReason = !(w.match_radius_m > 0)
    ? 'Радиус сопоставления должен быть больше нуля.'
    : !w.measure_plan && !w.measure_height && !w.measure_verticality
      ? 'Отметьте хотя бы одно отклонение.'
      : null;
  const noData = scheme.design.elements.length === 0 || scheme.survey.points.length === 0;
  const notMeasured = report?.items.filter((i) => i.status === 'not_measured') ?? [];
  // Отчёт хранится в JSONB: у рассчитанных до появления ролей «не элемент» поля нет.
  const ignored = report?.totals.ignored_points ?? 0;

  return (
    <StepShell
      n={7}
      lead="Допуски — по действующим СП. Проект может требовать жёстче (ГОСТ Р 51872 п.4.8): сверьте с проектом и ППГР и при необходимости переопределите. Числа считает код — модель их не трогает."
      save={{
        dirty: dirty && !readOnly,
        busy: saver.busy,
        error: saver.error,
        blockedReason,
        onSave: save,
        onReset: () => {
          workType.reset();
          work.reset();
          agreement.reset();
        },
      }}
    >
      <Fieldset disabled={readOnly}>
        <Stack $gap={4}>
          <Panel>
            <PanelHead>
              <PanelTitle>Тип работ</PanelTitle>
            </PanelHead>
            <Stack $gap={4}>
              <ChoiceGrid $min={150} role="radiogroup" aria-label="Тип работ">
                {WORK_TYPES.map((t) => (
                  <ChoiceCard
                    key={t.code}
                    name="geo-work-type"
                    checked={workType.draft === t.code}
                    onSelect={() => pickType(t.code)}
                    title={t.short}
                  >
                    {t.title}
                  </ChoiceCard>
                ))}
              </ChoiceGrid>
              {info?.description && <Hint>{info.description}</Hint>}
              {tolError && <ErrorText>Справочник допусков не загрузился.</ErrorText>}
              {info && info.rows.length > 0 && (
                <TableWrap>
                  <DataTable>
                    <thead>
                      <tr>
                        <th>Параметр</th>
                        <th>Допуск</th>
                        <th>Норматив</th>
                      </tr>
                    </thead>
                    <tbody>
                      {info.rows.map((r) => (
                        <tr key={r.code}>
                          <td>{r.title}</td>
                          <td>
                            <ToleranceValue>{r.value}</ToleranceValue>
                          </td>
                          <td>
                            <NormRef>{r.norm_ref}</NormRef>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </TableWrap>
              )}
              {info && (
                <Notice $tone="warning" role="note">
                  Сверьте с проектом / ППГР: если там допуск жёстче — впишите его ниже.
                </Notice>
              )}
            </Stack>
          </Panel>

          <Panel>
            <PanelHead>
              <PanelTitle>Что проставлять на схеме</PanelTitle>
            </PanelHead>
            <Stack $gap={4}>
              <Row $gap={5}>
                <Check
                  checked={w.measure_plan}
                  onChange={(v) => work.set({ ...w, measure_plan: v })}
                >
                  плановое положение
                </Check>
                <Check
                  checked={w.measure_height}
                  onChange={(v) => work.set({ ...w, measure_height: v })}
                >
                  высотные отметки
                </Check>
                <Check
                  checked={w.measure_verticality}
                  onChange={(v) => work.set({ ...w, measure_verticality: v })}
                >
                  вертикальность
                </Check>
              </Row>

              {workType.draft === 'piles' && (
                <FieldGrid $min={220}>
                  <FieldLabel>
                    Схема расположения свай
                    <Select
                      value={w.pile_layout}
                      onChange={(e) => {
                        const l = PILE_LAYOUTS.find((x) => x.value === e.target.value);
                        if (l) work.set({ ...w, pile_layout: l.value });
                      }}
                    >
                      {PILE_LAYOUTS.map((l) => (
                        <option key={l.value} value={l.value}>
                          {l.label}
                        </option>
                      ))}
                    </Select>
                    <FieldNote>
                      {PILE_LAYOUTS.find((l) => l.value === w.pile_layout)?.hint}
                    </FieldNote>
                  </FieldLabel>
                  <FieldLabel>
                    Сечение / диаметр сваи d, мм
                    <NumInput
                      value={w.element_size_mm}
                      onValue={(v) => work.set({ ...w, element_size_mm: v })}
                      placeholder="например, 300"
                    />
                    <FieldNote>Допуски в долях d. Пусто — берётся d элемента из РД.</FieldNote>
                  </FieldLabel>
                </FieldGrid>
              )}

              {workType.draft === 'km' && (
                <FieldGrid $min={220}>
                  <FieldLabel>
                    Высота колонн, м
                    <NumInput
                      value={w.column_height_m}
                      onValue={(v) => work.set({ ...w, column_height_m: v })}
                      placeholder="например, 12"
                    />
                    <FieldNote>
                      Допуск вертикальности зависит от высоты: 4–8 / 8–16 / 16–25 / 25–40 м.
                    </FieldNote>
                  </FieldLabel>
                </FieldGrid>
              )}

              <FieldGrid $min={220}>
                {workType.draft !== 'fill' && workType.draft !== 'cut' && (
                  <FieldLabel>
                    Проектные точки
                    <Select
                      value={w.contours ?? 'auto'}
                      onChange={(e) => {
                        const m = CONTOUR_MODES.find((x) => x.value === e.target.value);
                        if (m) work.set({ ...w, contours: m.value });
                      }}
                    >
                      {CONTOUR_MODES.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Select>
                    <FieldNote>
                      {CONTOUR_MODES.find((m) => m.value === (w.contours ?? 'auto'))?.hint}
                    </FieldNote>
                  </FieldLabel>
                )}
                <FieldLabel>
                  Радиус сопоставления, м
                  <NumInput
                    value={w.match_radius_m}
                    onValue={(v) => work.set({ ...w, match_radius_m: v ?? Number.NaN })}
                  />
                  <FieldNote>
                    Точка ищет ближайший проектный элемент (грань контура) в этом радиусе. Имена
                    точек учитываются, только если совпавшие точки лежат рядом с элементами.
                    Оголовок, снятый несколькими точками по кругу, сводится к центру окружности.
                  </FieldNote>
                </FieldLabel>
              </FieldGrid>
            </Stack>
          </Panel>

          <Panel>
            <PanelHead>
              <PanelTitle>Переопределение допусков</PanelTitle>
              <Muted>пусто — по СП</Muted>
            </PanelHead>
            <FieldGrid $min={170}>
              {OVERRIDE_FIELDS.map((f) => (
                <FieldLabel key={f.key}>
                  {f.label}
                  <NumInput
                    value={w.tolerance_overrides[f.key]}
                    onValue={(v) => setOverride(f.key, v)}
                    placeholder="по СП"
                  />
                </FieldLabel>
              ))}
            </FieldGrid>
          </Panel>
        </Stack>
      </Fieldset>

      <Panel aria-labelledby="geo-dev-title">
        <PanelHead>
          <div>
            <PanelTitle id="geo-dev-title">Отклонения</PanelTitle>
            {report && <Muted>Рассчитано {fmtDateTime(report.computed_at)}</Muted>}
          </div>
          {!readOnly && (
            <Button
              type="button"
              $variant="primary"
              onClick={() => void runCompute()}
              disabled={computing.busy || saver.busy || noData || Boolean(blockedReason)}
            >
              {computing.busy
                ? 'Считаем…'
                : dirty
                  ? 'Сохранить и рассчитать'
                  : report
                    ? 'Пересчитать'
                    : 'Рассчитать отклонения'}
            </Button>
          )}
        </PanelHead>
        <Stack $gap={4}>
          {noData && (
            <Notice $tone="neutral" role="status">
              Для расчёта нужны проектные элементы и точки съёмки — шаг 6.
            </Notice>
          )}
          {computing.error && <ErrorText role="alert">{computing.error}</ErrorText>}
          {report ? (
            <>
              {dirty && (
                <Notice $tone="warning" role="status">
                  Настройки изменены после расчёта — пересчитайте, чтобы таблица соответствовала им.
                </Notice>
              )}
              <DeviationTotals report={report} />
              {ignored > 0 && (
                <Muted>
                  Не элементы (реперы, станции): {ignored} — в сличении не участвуют. Роль задаётся
                  кодами точек на шаге 6.
                </Muted>
              )}
              <DeviationWarnings report={report} />
              <AppliedTolerance report={report} />
              <DeviationTable report={report} />
              {(report.unmatched_survey.length > 0 || notMeasured.length > 0) && (
                <FieldGrid $min={260}>
                  {report.unmatched_survey.length > 0 && (
                    <Notice $tone="warning" role="status">
                      <strong>Точки съёмки без пары · {report.unmatched_survey.length}</strong>
                      <Names>{report.unmatched_survey.join(', ')}</Names>
                    </Notice>
                  )}
                  {notMeasured.length > 0 && (
                    <Notice $tone="warning" role="status">
                      <strong>Проектные элементы без съёмки · {notMeasured.length}</strong>
                      <Names>{notMeasured.map(deviationItemLabel).join(', ')}</Names>
                    </Notice>
                  )}
                </FieldGrid>
              )}
            </>
          ) : (
            !noData && <Muted>Отклонения ещё не рассчитаны.</Muted>
          )}
        </Stack>
      </Panel>

      {showAgreement && (
        <Panel aria-labelledby="geo-agreement-title">
          <PanelHead>
            <PanelTitle id="geo-agreement-title">
              Согласование отклонений (ГОСТ Р 51872 п.5.5)
            </PanelTitle>
          </PanelHead>
          <Stack $gap={3}>
            <Hint>
              Есть превышения допусков — на схеме нужна надпись о согласовании с проектировщиком:
              организация, номер документа и дата. Без неё схему не передать в ПТО.
            </Hint>
            <Fieldset disabled={readOnly}>
              <FieldGrid $min={200}>
                <FieldLabel>
                  Организация-проектировщик
                  <Input
                    value={a.organization}
                    onChange={(e) => agreement.set({ ...a, organization: e.target.value })}
                  />
                </FieldLabel>
                <FieldLabel>
                  Документ (письмо, протокол) №
                  <Input
                    value={a.document}
                    onChange={(e) => agreement.set({ ...a, document: e.target.value })}
                  />
                </FieldLabel>
                <FieldLabel>
                  Дата
                  <Input
                    type="date"
                    value={a.date}
                    onChange={(e) => agreement.set({ ...a, date: e.target.value })}
                  />
                </FieldLabel>
              </FieldGrid>
            </Fieldset>
          </Stack>
        </Panel>
      )}
    </StepShell>
  );
}
